import React from 'react';
import {
  RefreshCw, AlertTriangle, CheckCircle2, Link2, Loader2, ShieldAlert, Mail, Sparkles, Radio,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useAuth } from '@/context/AuthContext';
import AppShell from '@/components/AppShell';
import { useLinkHealth } from '@/components/linkHealth/useLinkHealth';
import { StatCard, RunMetaRow, ScheduleBanner } from '@/components/linkHealth/parts';
import { BrokenUnitGroup } from '@/components/linkHealth/BrokenUnitGroup';

export default function AdminLinkHealth() {
  const { token } = useAuth();
  const {
    run, loading, polling, batchFixing, reverifying, scheduleInfo, suggestions,
    triggerRun, suggestFix, applyFix, dismissSuggestion, runBatchFix, runReverify, sendTestEmail,
  } = useLinkHealth(token);

  if (loading) {
    return (
      <AppShell>
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-[#14213D]" />
        </div>
      </AppShell>
    );
  }

  const summary = run?.summary || { total: 0, ok: 0, warning: 0, broken: 0 };
  const broken = run?.broken || [];

  const byUnit = broken.reduce((acc, b) => {
    const key = b.unit_title || 'Unknown';
    (acc[key] ||= []).push(b);
    return acc;
  }, {});

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] font-semibold mb-2">Admin</p>
            <h1 className="font-serif-display text-3xl text-[#14213D] tracking-tight mb-2">Link Health Dashboard</h1>
            <p className="text-sm text-[#5A6577] max-w-2xl">
              Continuous integrity check across every external URL stored in the curriculum &mdash; primary sources, YouTube videos, channels.
              Bot-blocked institutional archives (LoC, Smithsonian, British Museum, etc.) are surfaced as <em>warnings</em>, not failures, since real users on residential IPs see content fine.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {summary.broken > 0 && (
              <Button
                onClick={runReverify}
                disabled={reverifying || batchFixing || run?.status === 'running'}
                variant="outline"
                className="border-[#7A93AC] text-[#14213D] hover:bg-[#F0F4F8]"
                data-testid="reverify-btn"
                title="Re-check every still-broken URL with a 30 s timeout — drops URLs that now respond OK"
              >
                {reverifying ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Re-verifying&hellip;</>
                ) : (
                  <><Radio className="h-4 w-4 mr-2" />Re-verify ({summary.broken})</>
                )}
              </Button>
            )}
            {summary.broken > 0 && (
              <Button
                onClick={runBatchFix}
                disabled={batchFixing || reverifying || run?.status === 'running'}
                variant="outline"
                className="border-[#D4AF37] text-[#14213D] hover:bg-[#FBF6E8]"
                data-testid="batch-fix-btn"
                title="Run Fix with AI on every broken URL"
              >
                {batchFixing ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Batch Fixing&hellip;</>
                ) : (
                  <><Sparkles className="h-4 w-4 mr-2" />Batch Fix ({summary.broken})</>
                )}
              </Button>
            )}
            <Button
              onClick={sendTestEmail}
              variant="ghost"
              size="sm"
              className="text-[#5A6577]"
              data-testid="test-email-btn"
              title="Send a test email to verify Resend integration"
            >
              <Mail className="h-4 w-4 mr-1" />Test Email
            </Button>
            <Button
              onClick={triggerRun}
              disabled={run?.status === 'running'}
              className="btn-primary"
              data-testid="link-health-run-btn"
            >
              {run?.status === 'running' ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Running…</>
              ) : (
                <><RefreshCw className="h-4 w-4 mr-2" /> Run Now</>
              )}
            </Button>
          </div>
        </div>

        <RunMetaRow run={run} polling={polling} />
        <ScheduleBanner scheduleInfo={scheduleInfo} />

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          <StatCard label="URLs Checked" value={summary.total}   color="text-[#14213D]"  icon={Link2}        testid="stat-total" />
          <StatCard label="OK"           value={summary.ok}      color="text-emerald-700" icon={CheckCircle2} testid="stat-ok" />
          <StatCard label="Warnings"     value={summary.warning} color="text-[#14213D]"  icon={ShieldAlert}  testid="stat-warning" />
          <StatCard label="Broken"       value={summary.broken}  color="text-red-700"    icon={AlertTriangle} testid="stat-broken" />
        </div>

        {/* Broken URLs */}
        {!run && (
          <Card className="p-12 text-center" data-testid="empty-state">
            <Link2 className="h-12 w-12 text-[#CBD5E0] mx-auto mb-4" />
            <h3 className="text-lg font-serif text-[#14213D] mb-2">No link checks yet</h3>
            <p className="text-[#5A6577] mb-6">Run the first check to populate this dashboard. Usually under a minute.</p>
            <Button onClick={triggerRun} className="btn-primary">
              <RefreshCw className="h-4 w-4 mr-2" /> Run Now
            </Button>
          </Card>
        )}

        {run && summary.broken === 0 && (
          <Card className="p-10 text-center border-emerald-200 bg-emerald-50/50" data-testid="all-clear">
            <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto mb-4" />
            <h3 className="text-xl font-serif text-emerald-900 mb-1">All links healthy</h3>
            <p className="text-emerald-800 text-sm">
              {summary.ok} ok · {summary.warning} bot-blocked archive(s) (still work for end users)
            </p>
          </Card>
        )}

        {run && summary.broken > 0 && (
          <div className="space-y-6" data-testid="broken-urls-section">
            <h3 className="text-xl font-serif text-[#14213D] flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600" /> Broken URLs by Unit
              <span className="text-sm text-[#5A6577] font-sans font-normal ml-2">
                ({summary.broken} URLs in {Object.keys(byUnit).length} unit{Object.keys(byUnit).length === 1 ? '' : 's'})
              </span>
            </h3>
            {Object.entries(byUnit).map(([unitTitle, items]) => (
              <BrokenUnitGroup
                key={unitTitle}
                unitTitle={unitTitle}
                items={items}
                suggestions={suggestions}
                onSuggest={suggestFix}
                onApply={applyFix}
                onDismiss={dismissSuggestion}
              />
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}
